/* @jsx h */
// @ts-nocheck
import { defineComponent, h, ref, computed, onMounted, watch } from "vue";
import {
  DayWeekDateSelector,
  GanttChart,
  GanttGroup,
  Appointment,
  useLocalization,
} from "@lemmonhq/uikit";
import {
  daysOfWeek,
  formatDateTimeInTimezone,
  formatTimeInTz,
  sortTables,
} from "@lemmonhq/orders";
import {
  getRestaurantDateKey,
  getRestaurantWeekRangeUtc,
} from "@lemmonhq/blogic";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { api } from "@app/data/api";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useToast } from "@app/vue/providers/Toast";
import {
  listReservationRequests,
  listReservations,
} from "../../utils/trpcReservations";

import { CreateReservationForm } from "./components/CreateReservationForm";
import { ReservationDetails } from "./components/ReservationDetails";
import { WeeklySummary } from "./components/WeeklySummary";
import {
  FloorPlan,
  Reservation,
  ReservationRequest,
  TableStatus,
} from "@lemmonhq/types";
import { CreateReservationData } from "@app/server/routes/shop/routes";
import { useAuth } from "@app/vue/providers/Auth";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Format a Date object to ISO string in a specific timezone
 * This takes a UTC Date and converts it to the restaurant's timezone
 */
const formatDateInTimeZone = (date: Date, timeZone?: string) => {
  if (!timeZone) return date.toISOString();

  // Convert the Date to a dayjs object in the specified timezone
  // The date is already in UTC, so we just need to format it in the target timezone
  const dayjsDate = dayjs(date).tz(timeZone);

  // Return ISO 8601 format with timezone offset
  return dayjsDate.format();
};

const dateKeyToLocalDate = (dateKey: string) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

export const ReservationsSchedulePage = defineComponent({
  name: "ReservationsSchedulePage",
  setup() {
    const auth = useAuth();
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const restaurantTimeZone = computed(
      () =>
        auth.user.value?.restaurant.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    const selectedDate = ref(
      dateKeyToLocalDate(
        getRestaurantDateKey(new Date(), restaurantTimeZone.value),
      ),
    );
    const showCreateModal = ref(false);
    const createFormPrefill = ref<{
      date?: string;
      time?: string;
      tableId?: number;
      duration?: number;
    }>({});
    const showDetailsSheet = ref(false);
    const selectedReservation = ref<Reservation>();
    const reservations = ref<Reservation[]>([]);
    const tables = ref<TableStatus[]>([]);
    const loading = ref(true);
    const error = ref("");
    const floorPlans = ref<FloorPlan[]>();
    const reservationSchedule = ref<any>(null);

    // Expand/collapse all rooms
    const roomsCollapsed = ref(false);

    // Week view state
    const viewMode = ref<"day" | "week">("week");
    const weekReservations = ref<Record<string, Reservation[]>>({});
    const weekPendingRequests = ref<Record<string, ReservationRequest[]>>({});
    const weekLoading = ref(false);

    // Load data
    const loadData = async () => {
      try {
        loading.value = true;
        error.value = "";

        // Create all request promises
        const reservationsPromise = listReservations(trpc, {
          date: selectedDate.value,
          timezone: restaurantTimeZone.value,
          limit: "100",
        });
        const tablesPromise = api.getTablesMeta();
        const floorPlansPromise = api.getFloorPlans();

        // Run all in parallel
        const [reservationsResult, tablesResult, floorPlansResult] =
          await Promise.all([
            reservationsPromise,
            tablesPromise,
            floorPlansPromise,
          ]);

        // Assign data
        reservations.value = reservationsResult.data || [];
        tables.value = tablesResult;
        floorPlans.value = floorPlansResult;

        // Get reservation schedule from user settings
        const scheduleSetting = auth.user.value?.restaurant_settings?.find(
          (s) => s.key === "reservation_schedule",
        );
        if (scheduleSetting && scheduleSetting.value) {
          try {
            reservationSchedule.value =
              typeof scheduleSetting.value === "string"
                ? JSON.parse(scheduleSetting.value)
                : scheduleSetting.value;
            console.log("[Reservation Schedule]", reservationSchedule.value);
          } catch (e) {
            console.error("Failed to parse reservation schedule:", e);
          }
        }
      } catch (e) {
        console.error("Failed to load reservations:", e);
        error.value = t("reservations.general.failed_load");
        reservations.value = [];
      } finally {
        loading.value = false;
      }
    };

    // Load week data for weekly summary view
    const loadWeekData = async () => {
      weekLoading.value = true;
      try {
        const range = getRestaurantWeekRangeUtc(
          selectedDate.value,
          restaurantTimeZone.value,
        );

        const [reservationsResult, requestsResult] = await Promise.all([
          listReservations(trpc, {
            date_from: range.date_from,
            date_to: range.date_to,
            limit: "500",
          }),
          listReservationRequests(trpc, {
            status: "pending",
            date_from: range.date_from,
            date_to: range.date_to,
            limit: "500",
          }),
        ]);

        // Group reservations by date key (YYYY-MM-DD in restaurant timezone)
        const grouped: Record<string, Reservation[]> = {};
        for (const res of reservationsResult.data || []) {
          const d = dayjs(res.time).tz(restaurantTimeZone.value);
          const key = d.format("YYYY-MM-DD");
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(res);
        }
        weekReservations.value = grouped;

        // Group pending requests by date
        const pendingGrouped: Record<string, ReservationRequest[]> = {};
        for (const req of (requestsResult as any).data || []) {
          const d = dayjs(req.requested_time).tz(restaurantTimeZone.value);
          const key = d.format("YYYY-MM-DD");
          if (!pendingGrouped[key]) pendingGrouped[key] = [];
          pendingGrouped[key].push(req);
        }
        weekPendingRequests.value = pendingGrouped;
      } catch (e) {
        console.error("Failed to load week data:", e);
        weekReservations.value = {};
      } finally {
        weekLoading.value = false;
      }
    };

    // Computed: Monday of the week containing selectedDate
    const weekStart = computed(() => {
      const date = selectedDate.value;
      const dayOfWeek = date.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + mondayOffset,
      );
    });

    // Computed: extract service periods from reservation_schedule
    const servicePeriods = computed(() => {
      const schedule = reservationSchedule.value;
      if (!schedule) return [];

      for (const dayName of Object.keys(schedule)) {
        const daySchedule = schedule[dayName];
        if (Array.isArray(daySchedule) && daySchedule.length > 0) {
          return daySchedule.map((period: any) => ({
            name: period.name || "",
            start_time: period.start_time,
            end_time: period.end_time,
          }));
        }
      }
      return [];
    });

    // Computed: total number of tables
    const totalTables = computed(() => tables.value.length);

    // Handle day click from week view — switch to day mode
    const handleDayClick = (date: Date) => {
      selectedDate.value = date;
      viewMode.value = "day";
    };

    // Convert reservations to gantt chart appointments grouped by floor plans
    const ganttData = computed(() => {
      console.log("[GanttData] Computing gantt data with:", {
        reservationsCount: reservations.value.length,
        tablesCount: tables.value.length,
        restaurantTimeZone: restaurantTimeZone.value,
        selectedDate: selectedDate.value,
      });
      const groups: GanttGroup[] = [];

      // Group tables by floor plan
      const tablesByFloorPlan = new Map<string, TableStatus[]>();
      const unassignedTables: TableStatus[] = [];

      // First, organize all tables by floor plan
      tables.value.forEach((table) => {
        // Find which floor plan this table belongs to
        const floorPlan = floorPlans.value?.find((fp) =>
          fp.plan?.some((planTable) => planTable.tableId === table.id),
        );

        if (floorPlan) {
          if (!tablesByFloorPlan.has(floorPlan.id)) {
            tablesByFloorPlan.set(floorPlan.id, []);
          }
          tablesByFloorPlan.get(floorPlan.id)!.push(table);
        } else {
          unassignedTables.push(table);
        }
      });

      // Helper function to create appointments for a table
      const createAppointmentsForTable = (
        table: TableStatus,
      ): Appointment[] => {
        // Find all reservations that have this table assigned
        const activeStatuses = [
          "confirmed",
          "seated",
          "arrived",
          "partially-seated",
        ];
        const tableReservations = reservations.value.filter(
          (reservation) =>
            activeStatuses.includes(reservation.status) &&
            reservation.table_assignments?.some(
              (assignment) => assignment.table_id === table.id,
            ),
        );

        return tableReservations.map((reservation) => {
          // reservation.time is in UTC from the API
          // Create Date objects which will be in UTC
          const startTimeUTC = new Date(reservation.time);
          const endTimeUTC = new Date(
            startTimeUTC.getTime() + reservation.duration_mins * 60000,
          );

          // Format these UTC times to the restaurant's timezone for display
          // The formatDateInTimeZone function converts the UTC time to restaurant timezone
          const formattedStart = formatDateInTimeZone(
            startTimeUTC,
            restaurantTimeZone.value,
          );
          const formattedEnd = formatDateInTimeZone(
            endTimeUTC,
            restaurantTimeZone.value,
          );

          // Map status to type for color coding in Gantt
          const statusType =
            reservation.status === "seated" ||
            reservation.status === "arrived" ||
            reservation.status === "partially-seated"
              ? "walk-in" // green (success) for in-progress
              : reservation.status === "confirmed"
                ? "reservation" // blue (primary) for confirmed
                : "event"; // secondary for other

          return {
            id: reservation.id,
            name:
              reservation.customer_name ||
              reservation.party_name ||
              t("reservations.schedule.no_name"),
            subtitle: `${reservation.party_size}`,
            type: statusType,
            start_at: formattedStart,
            end_at: formattedEnd,
            data: reservation,
          };
        });
      };

      // Create groups for each floor plan
      floorPlans.value?.forEach((floorPlan) => {
        const floorPlanTables = tablesByFloorPlan.get(floorPlan.id) || [];
        // Sort tables using sortTables function for proper ordering
        const sortedFloorPlanTables = sortTables(floorPlanTables);

        // Always create the group, even if no tables
        groups.push({
          id: floorPlan.id,
          name: floorPlan.name,
          rows: sortedFloorPlanTables.map((table) => ({
            id: table.id.toString(),
            name: table.table.name,
            appointments: createAppointmentsForTable(table),
          })),
        });
      });

      // Add unassigned tables group if there are any
      if (unassignedTables.length > 0) {
        // Sort unassigned tables as well
        const sortedUnassignedTables = sortTables(unassignedTables);
        groups.push({
          id: "unassigned",
          name: t("reservations.schedule.unassigned"),
          rows: sortedUnassignedTables.map((table) => ({
            id: table.id.toString(),
            name: table.table.name,
            appointments: createAppointmentsForTable(table),
          })),
        });
      }

      return groups;
    });

    // Handle gantt chart selection
    const handleGanttSelect = (info: {
      groupId: string;
      rowId: string;
      time: string;
      appointment?: Appointment;
    }) => {
      if (info.appointment?.data) {
        // Clicked on an existing appointment
        selectedReservation.value = info.appointment.data;
        showDetailsSheet.value = true;
      } else {
        // Clicked on empty time slot - pre-fill date, time, and table
        const clickedTime = new Date(info.time);
        const d = dayjs(clickedTime).tz(restaurantTimeZone.value);
        const dateStr = d.format("YYYY-MM-DD");
        const timeStr = d.format("HH:mm");
        const tableId = info.rowId ? parseInt(info.rowId, 10) : undefined;

        createFormPrefill.value = {
          date: dateStr,
          time: timeStr,
          tableId: tableId && !isNaN(tableId) ? tableId : undefined,
        };
        showCreateModal.value = true;
      }
    };

    // Handle reservation creation
    const handleCreateReservation = async (formData: CreateReservationData) => {
      try {
        const created = await trpc.reservations.create.mutate(formData);
        if (created.tags_failed) {
          toast.showToast(t("reservations.details.tags_failed_on_create"), {
            type: "warning",
          });
        }
        showCreateModal.value = false;
        loadData(); // Refresh data
      } catch (e) {
        throw e; // Re-throw to let form handle error display
      }
    };

    // Handle reservation updates
    const handleReservationUpdate = (updatedReservation: Reservation) => {
      if (!updatedReservation?.id) return;

      reservations.value = reservations.value.map((reservation) =>
        reservation.id === updatedReservation.id
          ? updatedReservation
          : reservation,
      );

      if (selectedReservation.value?.id === updatedReservation.id) {
        selectedReservation.value = updatedReservation;
      }

      if (viewMode.value === "week") {
        loadWeekData();
      } else {
        loadData();
      }
    };

    // Get the day name for the selected date in the restaurant's timezone
    const getDayName = (date: Date, timezone?: string): string => {
      const days = daysOfWeek;
      const idx = dayjs(date)
        .tz(timezone || "UTC", true)
        .day();
      return days[idx];
    };

    // Calculate dead zones (closed periods) between service periods
    const deadZones = computed(() => {
      const dayName = getDayName(selectedDate.value, restaurantTimeZone.value);
      const daySchedule = reservationSchedule.value?.[dayName];

      if (
        !daySchedule ||
        !Array.isArray(daySchedule) ||
        daySchedule.length === 0
      ) {
        return [];
      }

      // Get the date string in restaurant timezone
      const selectedDay = dayjs(selectedDate.value).tz(
        restaurantTimeZone.value,
        true,
      );
      const dateStr = selectedDay.format("YYYY-MM-DD");

      // Sort periods by start time
      const sortedPeriods = [...daySchedule].sort((a, b) => {
        const [aHour, aMin] = a.start_time.split(":").map(Number);
        const [bHour, bMin] = b.start_time.split(":").map(Number);
        return aHour * 60 + aMin - (bHour * 60 + bMin);
      });

      const zones = [];

      // Find gaps between consecutive periods
      for (let i = 0; i < sortedPeriods.length - 1; i++) {
        const currentEnd = sortedPeriods[i].end_time;
        const nextStart = sortedPeriods[i + 1].start_time;

        // If there's a gap between end of current and start of next
        if (currentEnd !== nextStart) {
          const startTime = dayjs.tz(
            `${dateStr} ${currentEnd}`,
            restaurantTimeZone.value,
          );
          const endTime = dayjs.tz(
            `${dateStr} ${nextStart}`,
            restaurantTimeZone.value,
          );

          zones.push({
            start: startTime.format(),
            end: endTime.format(),
          });
        }
      }

      return zones;
    });

    // Set time range based on reservation schedule for the selected day
    const startTime = computed(() => {
      // Get the date string in YYYY-MM-DD format by formatting in restaurant timezone
      const selectedDay = dayjs(selectedDate.value).tz(
        restaurantTimeZone.value,
        true,
      );
      const dateStr = selectedDay.format("YYYY-MM-DD");

      // Get schedule for this day
      const dayName = getDayName(selectedDate.value, restaurantTimeZone.value);
      const daySchedule = reservationSchedule.value?.[dayName];

      // Always show full day (00:00 - 23:59), scroll to current time on load
      const startHour = 0;
      const startMinute = 0;

      // Create start time in restaurant's timezone using dayjs.tz
      // This creates a time on the selected date in the restaurant's timezone
      const dateInRestaurantTz = dayjs.tz(
        `${dateStr} ${String(startHour).padStart(2, "0")}:${String(startMinute).padStart(2, "0")}:00`,
        restaurantTimeZone.value,
      );

      const formatted = dateInRestaurantTz.format();
      return formatted;
    });

    const endTime = computed(() => {
      // Get the date string in YYYY-MM-DD format by formatting in restaurant timezone
      const selectedDay = dayjs(selectedDate.value).tz(
        restaurantTimeZone.value,
        true,
      );
      const dateStr = selectedDay.format("YYYY-MM-DD");

      // Get schedule for this day
      const dayName = getDayName(selectedDate.value, restaurantTimeZone.value);
      const daySchedule = reservationSchedule.value?.[dayName];

      // Always show full day
      const endHour = 23;
      const endMinute = 59;

      // Create end time in restaurant's timezone using dayjs.tz
      let dateInRestaurantTz = dayjs.tz(
        `${dateStr} ${String(endHour).padStart(2, "0")}:${String(endMinute).padStart(2, "0")}:59`,
        restaurantTimeZone.value,
      );

      // If the end hour is less than 6, assume it's the next day (e.g., 2:00 AM closing)
      if (endHour < 6) {
        dateInRestaurantTz = dateInRestaurantTz.add(1, "day");
      }

      const formatted = dateInRestaurantTz.format();
      return formatted;
    });

    // Watch for date changes
    watch(selectedDate, () => {
      if (viewMode.value === "week") {
        loadWeekData();
      } else {
        loadData();
      }
    });

    // Watch for view mode changes
    watch(viewMode, () => {
      if (viewMode.value === "week") {
        loadWeekData();
      } else {
        loadData();
      }
    });

    // Load initial data based on view mode
    onMounted(() => {
      if (viewMode.value === "week") {
        loadWeekData();
      } else {
        loadData();
      }
    });

    return () => (
      <div class="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <AppSubpageHeader
          icon="calendar_month"
          title={t("reservations.nav.schedule")}
          description={t("reservations.schedule.manage_reservations")}
        >
          {{
            center: () => (
              <div class="flex items-center gap-3">
                <DayWeekDateSelector
                  selectedDate={selectedDate.value}
                  onDateChange={(date) => (selectedDate.value = date)}
                  viewMode={viewMode.value}
                  onViewModeChange={(mode) => (viewMode.value = mode)}
                />
              </div>
            ),
            actions: () => (
              <div class="flex items-center gap-3">
                {/* Expand/Collapse toggle */}
                {viewMode.value === "day" && (
                  <div
                    class="relative flex items-stretch rounded-lg overflow-hidden flex-shrink-0"
                    style="height: 30px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08);"
                  >
                    <div
                      class="absolute top-[2px] bottom-[2px] transition-transform duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                      style={{
                        width: "calc(50% - 2px)",
                        left: "2px",
                        transform: roomsCollapsed.value
                          ? "translateX(calc(100% + 2px))"
                          : "translateX(0)",
                      }}
                    >
                      <div
                        class="h-full rounded-md"
                        style="background: rgba(255,255,255,0.12); box-shadow: 0 1px 3px rgba(0,0,0,0.3);"
                      />
                    </div>
                    <button
                      class={`relative z-10 flex items-center justify-center px-3 text-[11px] font-medium tracking-wide transition-all duration-300 cursor-pointer ${
                        !roomsCollapsed.value
                          ? "text-accent/90"
                          : "text-base-content/30 hover:text-base-content/50"
                      }`}
                      onClick={() => (roomsCollapsed.value = false)}
                    >
                      <span class="material-symbols-outlined text-[15px]">
                        expand_all
                      </span>
                    </button>
                    <button
                      class={`relative z-10 flex items-center justify-center px-3 text-[11px] font-medium tracking-wide transition-all duration-300 cursor-pointer ${
                        roomsCollapsed.value
                          ? "text-accent/90"
                          : "text-base-content/30 hover:text-base-content/50"
                      }`}
                      onClick={() => (roomsCollapsed.value = true)}
                    >
                      <span class="material-symbols-outlined text-[15px]">
                        collapse_all
                      </span>
                    </button>
                  </div>
                )}
                <button
                  onClick={() => {
                    createFormPrefill.value = {};
                    showCreateModal.value = true;
                  }}
                  class="btn btn-primary btn-sm gap-1.5 shadow-lg hover:shadow-primary/25 transition-all"
                  style="height: 36px;"
                >
                  <span class="material-symbols-outlined text-base">add</span>
                  <span class="hidden lg:flex">New</span>
                </button>
              </div>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          {/* Content */}
          <AppSubpageContent noPadding={true}>
            <div class="flex flex-col flex-1 min-h-0 h-full overflow-x-hidden">
              {loading.value && viewMode.value === "day" ? (
                <div class="flex items-center justify-center h-full">
                  <div class="text-center">
                    <div class="relative">
                      <div class="animate-spin rounded-full h-16 w-16 border-4 border-primary/20 border-t-primary mx-auto"></div>
                      <span class="material-symbols-outlined text-2xl text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        calendar_month
                      </span>
                    </div>
                    <p class="mt-4 text-base-content/60">
                      {t("reservations.general.loading_reservations")}
                    </p>
                  </div>
                </div>
              ) : error.value && viewMode.value === "day" ? (
                <div class="flex items-center justify-center h-full">
                  <div class="text-center bg-base-200 rounded-2xl p-8 shadow-lg max-w-md">
                    <div class="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span class="material-symbols-outlined text-4xl text-error">
                        error
                      </span>
                    </div>
                    <h3 class="text-lg font-semibold text-base-content mb-2">
                      {t("reservations.general.error_loading")}
                    </h3>
                    <p class="text-base-content/60 mb-6">{error.value}</p>
                    <button
                      onClick={() => loadData()}
                      class="btn btn-primary gap-2"
                    >
                      <span class="material-symbols-outlined text-lg">
                        refresh
                      </span>
                      {t("reservations.general.try_again")}
                    </button>
                  </div>
                </div>
              ) : viewMode.value === "week" ? (
                <div class="p-4 xl:p-6">
                  <WeeklySummary
                    weekStart={weekStart.value}
                    reservationsByDay={weekReservations.value}
                    pendingByDay={weekPendingRequests.value}
                    totalTables={totalTables.value}
                    servicePeriods={servicePeriods.value}
                    timezone={restaurantTimeZone.value}
                    onDayClick={handleDayClick}
                    loading={weekLoading.value}
                  />
                </div>
              ) : (
                <div class="h-full">
                  <GanttChart
                    groups={ganttData.value}
                    startTime={startTime.value}
                    endTime={endTime.value}
                    intervalMinutes={30}
                    columnWidth={80}
                    collapseAll={roomsCollapsed.value}
                    showCurrentTime={true}
                    autoScrollToCurrentTime={true}
                    currentTimeZone={restaurantTimeZone.value}
                    deadZones={deadZones.value}
                    onSelect={handleGanttSelect}
                  />
                </div>
              )}
            </div>
          </AppSubpageContent>

          {/* Create Reservation Modal */}
          {showCreateModal.value ? (
            <CreateReservationForm
              onSubmit={handleCreateReservation}
              onCancel={() => (showCreateModal.value = false)}
              initialDate={createFormPrefill.value.date}
              initialTime={createFormPrefill.value.time}
              initialTableId={createFormPrefill.value.tableId}
              initialDuration={createFormPrefill.value.duration}
            />
          ) : null}
          {showDetailsSheet.value && selectedReservation.value ? (
            <ReservationDetails
              reservation={selectedReservation.value}
              onClose={() => (showDetailsSheet.value = false)}
              onUpdate={handleReservationUpdate}
            />
          ) : null}
        </AppSubpage>
      </div>
    );
  },
});
